import {useState, useCallback, useMemo, useRef, useEffect} from 'react'
import {useFormContext} from 'react-hook-form'

import {MaterialFormDefaultValues} from '../../../materials-details/ExistingMaterialDetails/hooks'
import {StrengthTarget} from '../types'
import {
  transformMeasurementTargetsToStrengthTargets,
  transformStrengthTargetsToMeasurementTargets
} from '../utils/measurementTargetsTransformers'
import {
  upsertTarget,
  recalculateValidToDates,
  deleteTarget,
  markCurrentTargets,
  sortTargetsByValidity
} from '../utils/strengthTargetsUtils'

import {useGlobalMeasurementTypesQuery} from '@settings/modules/measurements/hooks/queries/useGlobalMeasurementTypesQuery'

interface UseStrengthTargetsManagementResult {
  targets: StrengthTarget[]
  backendTargets: StrengthTarget[]
  isFormOpen: boolean
  editingTarget: StrengthTarget | undefined
  openNewTargetForm: () => void
  openEditForm: (target: StrengthTarget) => void
  closeForm: () => void
  saveTarget: (target: StrengthTarget) => void
  confirmDelete: (target: StrengthTarget) => void
  getTargetsForDay: (dayPeriod: number) => StrengthTarget[]
}

export function useStrengthTargetsManagement(timezone: string): UseStrengthTargetsManagementResult {
  const {watch, setValue, formState} = useFormContext<MaterialFormDefaultValues>()
  const measurementTargets = watch('measurementTargets')

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingTarget, setEditingTarget] = useState<StrengthTarget | undefined>(undefined)

  const {data: globalMeasurementTypes = []} = useGlobalMeasurementTypesQuery()

  const baseTargets = useMemo(() => {
    return transformMeasurementTargetsToStrengthTargets(measurementTargets, globalMeasurementTypes)
  }, [measurementTargets, globalMeasurementTypes])

  const targets = useMemo(() => {
    return markCurrentTargets(baseTargets, timezone)
  }, [baseTargets, timezone])

  const backendTargetsRef = useRef<StrengthTarget[]>([])
  const isInitializedRef = useRef(false)

  useEffect(() => {
    const hasData = globalMeasurementTypes.length > 0 && measurementTargets !== undefined
    const isFormDirty = formState.isDirty

    if (hasData && !isInitializedRef.current && !isFormDirty) {
      backendTargetsRef.current = transformMeasurementTargetsToStrengthTargets(
        measurementTargets,
        globalMeasurementTypes
      )
      isInitializedRef.current = true
    }
  }, [measurementTargets, globalMeasurementTypes, formState.isDirty])

  const backendTargets = backendTargetsRef.current

  const openNewTargetForm = useCallback(() => {
    setEditingTarget(undefined)
    setIsFormOpen(true)
  }, [])

  const openEditForm = useCallback((target: StrengthTarget) => {
    setEditingTarget(target)
    setIsFormOpen(true)
  }, [])

  const closeForm = useCallback(() => {
    setIsFormOpen(false)
    setEditingTarget(undefined)
  }, [])

  const saveTarget = useCallback(
    (target: StrengthTarget) => {
      const isUpdate = !!editingTarget

      const updatedTargets = recalculateValidToDates(
        upsertTarget(targets, target, isUpdate),
        target.dayPeriod,
        target.id
      )

      const updatedMeasurementTargets = transformStrengthTargetsToMeasurementTargets(
        updatedTargets,
        globalMeasurementTypes
      )

      setValue('measurementTargets', updatedMeasurementTargets, {shouldDirty: true})
    },
    [targets, editingTarget, globalMeasurementTypes, setValue]
  )

  const confirmDelete = useCallback(
    (targetToDelete: StrengthTarget) => {
      const updatedTargets = deleteTarget(targets, targetToDelete)

      const updatedMeasurementTargets = transformStrengthTargetsToMeasurementTargets(
        updatedTargets,
        globalMeasurementTypes
      )

      setValue('measurementTargets', updatedMeasurementTargets, {shouldDirty: true})
    },
    [targets, globalMeasurementTypes, setValue]
  )

  const getTargetsForDay = useCallback(
    (dayPeriod: number) => {
      const filtered = targets.filter((t) => t.dayPeriod === dayPeriod)
      return sortTargetsByValidity(filtered, timezone)
    },
    [targets, timezone]
  )

  return {
    targets,
    backendTargets,
    isFormOpen,
    editingTarget,
    openNewTargetForm,
    openEditForm,
    closeForm,
    saveTarget,
    confirmDelete,
    getTargetsForDay
  }
}
